import React from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowTopRightOnSquareIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import useAnalytics from "../../hooks/useAnalytics";
import ReadingHistoryTab from "./ReadingHistoryTab";
import WishlistTab from "./WishlistTab";
import OrderHistoryTab from "./OrderHistoryTab";
import UserAnalyticsTab from "./UserAnalyticsTab";
import RewardPointsTab from "./RewardPointsTab";

export default function AnalyticsPanel({ selectedMember, isExpandedView, onToggleExpand }) {
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const {
        selected,
        setSelected,
        loading,
        orders,
        readingHistory,
        readingTotal,
        userAnalytics,
        wishlist,
        rewardPoints,
        toast,
        expandedOrders,
        toggleOrderExpand
    } = useAnalytics(selectedMember, token);

    const options = [
        "Reading History",
        "Wishlist",
        "Order History",
        "User Analytics",
        "Reward Points",
    ];

    const renderActiveData = () => {
        if (!selectedMember?.id) {
            return (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm font-semibold uppercase tracking-wider">
                    Please select a member first
                </div>
            );
        }

        switch (selected) {
            case "Order History":
                return (
                    <OrderHistoryTab
                        loading={loading}
                        orders={orders}
                        expandedOrders={expandedOrders}
                        onToggleOrderExpand={toggleOrderExpand}
                    />
                );
            case "Reading History":
                return (
                    <ReadingHistoryTab
                        loading={loading}
                        readingHistory={readingHistory}
                        readingTotal={readingTotal}
                    />
                );
            case "User Analytics":
                return (
                    <UserAnalyticsTab
                        loading={loading}
                        userAnalytics={userAnalytics}
                    />
                );
            case "Wishlist":
                return (
                    <WishlistTab
                        loading={loading}
                        wishlist={wishlist}
                    />
                );
            case "Reward Points":
                return (
                    <RewardPointsTab
                        loading={loading}
                        rewardPoints={rewardPoints}
                    />
                );
            default:
                return (
                    <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                        <p className="text-sm font-semibold uppercase tracking-wider">API Not Integrated Yet</p>
                    </div>
                );
        }
    };

    return (
        <div className="bg-white p-5 rounded-lg shadow-xs border border-gray-200">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <select
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                        className="border border-gray-250 rounded-md px-3 py-2 text-sm outline-none text-gray-800"
                    >
                        {options.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>

                    {selectedMember?.id && (selected === "Order History" || selected === "Reading History" || selected === "User Analytics" || selected === "Wishlist" || selected === "Reward Points") && (
                        <button
                            onClick={onToggleExpand}
                            className="p-2 border border-gray-250 rounded-md hover:bg-gray-50 flex items-center justify-center bg-white shadow-xs transition-colors"
                            title={isExpandedView ? "Go back to Checkout" : "Expand View"}
                        >
                            {isExpandedView ? (
                                <XMarkIcon className="h-5 w-5 text-gray-800 stroke-[2]" />
                            ) : (
                                <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-800 stroke-[2]" />
                            )}
                        </button>
                    )}
                </div>

                <button
                    onClick={() => {
                        if (!selectedMember?.id) {
                            return;
                        }

                        navigate("/manage-subscription", {
                            state: { member: selectedMember },
                        });
                    }}
                    disabled={!selectedMember?.id}
                    className={`border px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                        selectedMember?.id
                            ? "border-blue-500 text-blue-500 hover:bg-gray-50 cursor-pointer"
                            : "border-gray-300 text-gray-300 cursor-not-allowed"
                    }`}
                >
                    Manage Subscription
                </button>
            </div>

            {/* Panel */}
            <div className={`border border-gray-150 rounded-lg p-4 bg-gray-50/20 ${isExpandedView ? "max-h-[600px] h-[550px]" : "max-h-[350px]"} overflow-y-auto relative`}>
                {renderActiveData()}
            </div>

            {/* Toast Notifications */}
            {toast && (
                <div className="fixed bottom-4 right-4 bg-black text-white px-4 py-2.5 rounded-lg shadow-lg z-50 text-xs font-bold tracking-wide border border-gray-800 animate-slide-in">
                    {toast}
                </div>
            )}
        </div>
    );
}
